<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Key, AlertCircle, CheckCircle, ArrowRight } from 'lucide-vue-next'
import { toast } from '@/utils/toast'
import { tenantApi } from '@/services/api'
import { useTenantStore } from '@/stores/tenant'
import type { Tenant, Base } from '@/types'

const { t } = useI18n()
const router = useRouter()
const tenantStore = useTenantStore()

const companyName = ref('')
const personalBaseToken = ref('')
const currentBase = ref<Base | null>(null)
const currentTenant = ref<Tenant | null>(null)
const templateCount = ref(0)
const saving = ref(false)

async function loadData(): Promise<void> {
  try {
    // Fetch tenant and current base info
    const { tenant, base, templateCount: count } = await tenantApi.getTenantInfo()

    currentTenant.value = tenant
    currentBase.value = base
    templateCount.value = count
    companyName.value = tenant.name || ''
    personalBaseToken.value = base?.personal_base_token || ''
  } catch (error) {
    console.error('Failed to load data:', error)
    toast.error(error instanceof Error ? error.message : t('failedToLoadData'))
  }
}

async function saveSettings(): Promise<void> {
  if (!personalBaseToken.value.trim()) {
    toast.error(t('personalBaseTokenRequired'))
    return
  }

  try {
    saving.value = true
    const result = await tenantApi.updateTenantInfo({
      name: companyName.value,
      personal_base_token: personalBaseToken.value
    })

    // Update local state
    currentTenant.value = result.tenant
    currentBase.value = result.base
    templateCount.value = result.templateCount
    // Update store with both tenant and base info
    tenantStore.setTenantAndBase(result.tenant, result.base)

    toast.success(t('companyInfoSaved'))
  } catch (error: any) {
    console.error('Failed to save settings:', error)

    // Show the actual error message from the API
    toast.error(error instanceof Error ? error.message : t('failedToSaveTenantInfo'))
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadData()
})
</script>

<template>
  <div class="h-full bg-white flex flex-col overflow-hidden">
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-2xl mx-auto w-full p-8">
        <!-- Content -->
        <div class="space-y-6">
          <!-- Warning if base missing token -->
          <div v-if="!currentBase?.personal_base_token" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <AlertCircle :size="20" class="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p class="font-semibold text-yellow-800">{{ t('setupRequired') }}</p>
                <p class="text-sm text-yellow-700 mt-1">{{ t('setupRequiredMessage') }}</p>
              </div>
            </div>
          </div>

          <!-- Settings Form -->
          <div class="bg-gray-50 rounded-lg p-6 space-y-5">
            <!-- Company Name -->
            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-2">
                {{ t('companyName') }}
                <span class="text-gray-500 font-normal ml-1">({{ t('optional') }})</span>
              </label>
              <input
                v-model="companyName"
                type="text"
                :placeholder="t('companyNamePlaceholder')"
                class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <!-- Personal Base Token -->
            <div>
              <label class="block text-sm font-semibold text-gray-800 mb-2">
                {{ t('personalBaseToken') }}
              </label>

              <!-- Help Text -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <div class="flex items-start gap-2">
                  <Key :size="16" class="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div class="text-xs text-blue-800 flex-1">
                    <p class="font-semibold mb-1">{{ t('whatIsPersonalBaseToken') }}</p>
                    <p class="leading-relaxed mb-2">{{ t('personalBaseTokenHelp') }}</p>
                    <a
                      href="https://ksi-digital.jp.larksuite.com/wiki/JRa7wPLwjiZFLWk1hFijJRQppxg#share-QCcbd5YIHopRj6xxLPPjN9gRp8f"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium hover:underline"
                    >
                      {{ t('viewSetupGuide') }}
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              <div class="relative">
                <input
                  v-model="personalBaseToken"
                  type="password"
                  :placeholder="t('personalBaseTokenPlaceholder')"
                  class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <div v-if="currentBase?.personal_base_token" class="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle :size="18" class="text-green-500" />
                </div>
              </div>
            </div>

            <!-- Save Button -->
            <div class="flex justify-end pt-2">
              <button
                @click="saveSettings"
                :disabled="saving"
                class="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ saving ? t('saving') : t('save') }}
              </button>
            </div>
          </div>

          <!-- Usage & Limits Card -->
          <div class="bg-gray-50 rounded-lg p-6 space-y-5">
            <h3 class="text-lg font-semibold text-gray-900">{{ t('usageAndLimits') }}</h3>

            <!-- Template Limit -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-semibold text-gray-800">
                  {{ t('templateLimit') }}
                </label>
                <button
                  @click="router.push('/my-templates')"
                  class="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  {{ t('viewMyTemplates') }}
                  <ArrowRight :size="16" />
                </button>
              </div>
              <div class="bg-white border border-gray-300 rounded-lg p-4">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600">{{ t('templatesUsed') }}</span>
                  <span class="text-sm font-semibold" :class="templateCount >= (currentTenant?.max_templates || 0) ? 'text-red-600' : 'text-gray-800'">
                    {{ t('currentTemplateCount', { current: templateCount, max: currentTenant?.max_templates || 0 }) }}
                  </span>
                </div>
                <div v-if="templateCount >= (currentTenant?.max_templates || 0)" class="mt-2 text-xs text-red-600">
                  {{ t('templateLimitReached', { current: templateCount, max: currentTenant?.max_templates || 0 }) }}
                </div>
              </div>
            </div>

            <!-- Monthly Generation Quota -->
            <div v-if="currentTenant">
              <label class="block text-sm font-semibold text-gray-800 mb-2">
                {{ t('monthlyGenerationQuota') }}
              </label>
              <div class="bg-white border border-gray-300 rounded-lg p-4">
                <!-- Unlimited Quota (null) -->
                <div v-if="currentTenant.monthly_quota === null">
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ t('generationsThisMonth') }}</span>
                    <span class="text-sm font-semibold text-gray-800">
                      {{ currentTenant.current_quota || 0 }}
                    </span>
                  </div>
                  <div class="mt-2 text-xs text-green-600 italic">
                    Unlimited quota
                  </div>
                </div>

                <!-- Limited Quota (with number) -->
                <div v-else>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ t('generationsThisMonth') }}</span>
                    <span class="text-sm font-semibold"
                      :class="{
                        'text-red-600': (currentTenant?.current_quota || 0) >= (currentTenant?.monthly_quota || 0),
                        'text-yellow-600': (currentTenant?.current_quota || 0) >= (currentTenant?.monthly_quota || 0) * 0.8 && (currentTenant?.current_quota || 0) < (currentTenant?.monthly_quota || 0),
                        'text-gray-800': (currentTenant?.current_quota || 0) < (currentTenant?.monthly_quota || 0) * 0.8
                      }"
                    >
                      {{ t('currentQuotaCount', { current: currentTenant?.current_quota || 0, max: currentTenant?.monthly_quota || 0 }) }}
                    </span>
                  </div>
                  <div v-if="currentTenant?.quota_reset_day_number" class="mt-2 text-xs text-gray-600">
                    {{ t('quotaResetsOn', { day: currentTenant.quota_reset_day_number }) }}
                  </div>
                  <div v-if="(currentTenant?.current_quota || 0) >= (currentTenant?.monthly_quota || 0)" class="mt-2 text-xs text-red-600 font-medium">
                    {{ t('quotaExceededWarning') }}
                  </div>
                  <div v-else-if="(currentTenant?.current_quota || 0) >= (currentTenant?.monthly_quota || 0) * 0.8" class="mt-2 text-xs text-yellow-600">
                    Approaching quota limit ({{ Math.round(((currentTenant?.current_quota || 0) / (currentTenant?.monthly_quota || 1)) * 100) }}%)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
